import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Tooltip,
  Fab,
  LinearProgress,
} from '@mui/material';
import {
  People,
  AccessTime,
  EventBusy,
  TrendingUp,
  Search,
  EventNote,
  Home,
  Business,
  Refresh,
  MoreVert,
  Visibility,
  CheckCircle,
  Cancel,
  Coffee,
  Schedule,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { dashboardAPI, attendanceAPI } from '../services/api';
import { SocketContext } from '../App';
import { getTodayStr, localDateStr } from '../utils/dateHelper';

const AdminDashboard = () => {
  const socket = React.useContext(SocketContext);
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Action menu state
  const [actionAnchor, setActionAnchor] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Status change dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // View details dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(null);

  // Real-time update timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      if (!statusDialogOpen && !viewDialogOpen) {
        loadEmployees();
      }
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadDashboardData();
    
    if (socket) {
      socket.on('employee_status', (data) => {
        console.log('Employee status update:', data);
        loadEmployees();
        loadStats();
      });
      
      socket.on('attendance_update', (data) => {
        console.log('Attendance update:', data);
        loadDashboardData();
      });
      
      return () => {
        socket.off('employee_status');
        socket.off('attendance_update');
      };
    }
  }, [socket]);

  const loadDashboardData = async () => {
    try {
      setError(null);
      setRefreshing(true);
      const [statsRes, realtimeRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRealtime(),
      ]);

      if (statsRes.data && statsRes.data.success) {
        setStats(statsRes.data.data);
      }
      if (realtimeRes.data && realtimeRes.data.success && realtimeRes.data.data) {
        processEmployees(realtimeRes.data.data.employees || []);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
      setSnackbar({ open: true, message: 'Failed to load dashboard data', severity: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await dashboardAPI.getRealtime();
      if (response.data && response.data.success && response.data.data) {
        processEmployees(response.data.data.employees || []);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      if (response.data && response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const processEmployees = (employeeData) => {
    const today = getTodayStr();

     const processed = employeeData.map(emp => {
       let status = 'absent';

       switch (emp.status) {
         case 'online':
           status = emp.is_late ? 'late' : 'present';
           break;
         case 'present':
         case 'late':
         case 'on_break':
         case 'on_leave':
         case 'logged_out':
         case 'holiday':
           status = emp.status;
           break;
         case 'offline':
         case 'absent':
         default:
           status = 'absent';
           break;
       }
      
      let netWorkingTime = emp.net_working_time || 0;

      const loginDate = emp.login_time ? new Date(emp.login_time).toISOString().split('T')[0] : null;

      if (emp.login_time && !emp.logout_time && loginDate === today) {
        const loginTime = new Date(emp.login_time);
        const totalElapsed = Math.floor((new Date() - loginTime) / 60000);
        let breakTime = emp.total_break_time || 0;
        
        if (emp.breaks && Array.isArray(emp.breaks)) {
          emp.breaks.forEach(brk => {
            if (brk.status === 'active' && brk.start_time) {
              const breakStart = new Date(brk.start_time);
              const breakElapsed = Math.floor((new Date() - breakStart) / 60000);
              breakTime += breakElapsed;
            } else if (brk.duration) {
              breakTime += brk.duration;
            }
          });
        }
        
        netWorkingTime = Math.max(0, totalElapsed - breakTime);
      }
      
      return {
        ...emp,
        breaks: emp.breaks || [],
        net_working_time: netWorkingTime,
        total_break_time: emp.total_break_time || 0,
        login_time: emp.login_time || null,
        logout_time: emp.logout_time || null,
        status: status,
        work_type: emp.work_type || 'wfo',
        is_on_break: emp.is_on_break || false,
        is_late: emp.is_late || false,
      };
    });
    
    setEmployees(processed);
  };

  const calculateBreakTime = (emp) => {
    if (!emp) return 0;
    
    let totalMinutes = 0;
    
    if (emp.breaks && Array.isArray(emp.breaks) && emp.breaks.length > 0) {
      emp.breaks.forEach(brk => {
        if (brk.status === 'completed' && brk.duration) {
          totalMinutes += brk.duration;
        } else if (brk.status === 'active' && brk.start_time) {
          const startTime = new Date(brk.start_time);
          const elapsed = Math.floor((currentTime - startTime) / 60000);
          totalMinutes += elapsed;
        } else if (brk.end_time && brk.start_time && brk.status !== 'active') {
          const breakStart = new Date(brk.start_time);
          const breakEnd = new Date(brk.end_time);
          totalMinutes += Math.floor((breakEnd - breakStart) / 60000);
        }
      });
    } else {
      totalMinutes = emp.total_break_time || 0;
    }
    
    return totalMinutes;
  };

  const calculateWorkingTime = (emp) => {
    if (!emp) return 0;
    
    if (emp.logout_time) {
      return emp.net_working_time || 0;
    }
    
    if (!emp.login_time) {
      return 0;
    }
    
    const today = getTodayStr();
    const loginDate = emp.login_time ? localDateStr(new Date(emp.login_time).getFullYear(), new Date(emp.login_time).getMonth() + 1, new Date(emp.login_time).getDate()) : null;
    
    if (loginDate !== today) {
      return emp.net_working_time || 0;
    }
    
    const loginTime = new Date(emp.login_time);
    const totalMinutes = Math.floor((currentTime - loginTime) / 60000);
    const breakMinutes = calculateBreakTime(emp);
    
    return Math.max(0, totalMinutes - breakMinutes);
  };

  const formatWorkingTime = (emp) => {
    const minutes = calculateWorkingTime(emp);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatBreakTime = (emp) => {
    const minutes = calculateBreakTime(emp);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
      case 'online':
      case 'logged_out':
        return { bgcolor: 'rgba(76, 175, 80, 0.15)', color: '#66BB6A', border: '1px solid #66BB6A' };
      case 'late':
        return { bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#FFB74D', border: '1px solid #FFB74D' };
      case 'on_break':
        return { bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#FFB74D', border: '1px solid #FFB74D' };
      case 'on_leave':
        return { bgcolor: 'rgba(66, 165, 245, 0.15)', color: '#64B5F6', border: '1px solid #64B5F6' };
      case 'absent':
      case 'offline':
        return { bgcolor: 'rgba(239, 83, 80, 0.15)', color: '#EF5350', border: '1px solid #EF5350' };
      case 'holiday':
        return { bgcolor: 'rgba(156, 39, 176, 0.15)', color: '#CE93D8', border: '1px solid #CE93D8' };
      default:
        return { bgcolor: 'rgba(100, 100, 100, 0.15)', color: '#B0BEC5', border: '1px solid #B0BEC5' };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present':
      case 'online':
        return 'Present';
      case 'logged_out':
        return 'Logged Out';
      case 'late':
        return 'Late';
      case 'on_break':
        return 'On Break';
      case 'on_leave':
        return 'On Leave';
      case 'absent':
      case 'offline':
        return 'Absent';
      case 'holiday':
        return 'Holiday';
      default:
        return status || 'Unknown';
    }
  };

  const formatTime = (time) => {
    if (!time) return '-';
    try {
      const date = new Date(time);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (e) {
      return '-';
    }
  };

  const handleActionClick = (event, employee) => {
    event.preventDefault();
    event.stopPropagation();
    setActionAnchor(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleActionClose = () => {
    setActionAnchor(null);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setStatusDialogOpen(true);
    handleActionClose();
  };

  const handleStatusSubmit = async () => {
    if (!selectedEmployee || !selectedStatus) return;
    
    setStatusLoading(true);
    try {
      const attendanceId = selectedEmployee.attendance_id;
      const today = new Date().toISOString().split('T')[0];
      
      let response;
      if (attendanceId) {
        response = await attendanceAPI.updateStatus(attendanceId, selectedStatus);
      } else {
        try {
          response = await attendanceAPI.create({
            employee_id: selectedEmployee.id,
            date: today,
            status: selectedStatus
          });
        } catch (createError) {
          if (createError.response?.status === 409) {
            setSnackbar({ 
              open: true, 
              message: 'Attendance record already exists for this employee today. Refreshing...', 
              severity: 'warning' 
            });
            await loadDashboardData();
            setStatusLoading(false);
            setStatusDialogOpen(false);
            return;
          }
          throw createError;
        }
      }
      
      if (response.data && response.data.success) {
        setSnackbar({ 
          open: true, 
          message: `${selectedEmployee.first_name} ${selectedEmployee.last_name} marked as ${selectedStatus.replace('_', ' ')} successfully`, 
          severity: 'success' 
        });
        await loadDashboardData();
      } else {
        throw new Error(response.data?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || error.message || 'Error updating status', 
        severity: 'error' 
      });
    } finally {
      setStatusLoading(false);
      setStatusDialogOpen(false);
    }
  };

  const handleViewDetails = (employee) => {
    setViewEmployee(employee);
    setViewDialogOpen(true);
    handleActionClose();
  };

  const handleRefresh = () => {
    loadDashboardData();
    setSnackbar({ open: true, message: 'Refreshing data...', severity: 'info' });
  };

  const matchesStatusFilter = (emp) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'present') return ['present', 'late', 'logged_out'].includes(emp.status);
    if (statusFilter === 'away') return ['on_leave', 'absent'].includes(emp.status);
    return emp.status === statusFilter;
  };

  const filteredEmployees = employees.filter(emp => {
    const searchText = `${emp.first_name} ${emp.last_name} ${emp.employee_id || ''} ${emp.department || ''} ${emp.email || ''}`.toLowerCase();
    return searchText.includes(searchTerm.toLowerCase()) && matchesStatusFilter(emp);
  });

  const statusCounts = employees.reduce((acc, emp) => {
    const status = emp.status || 'absent';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const presentCount = (statusCounts.present || 0) + (statusCounts.late || 0) + (statusCounts.logged_out || 0);
  const onBreakCount = statusCounts.on_break || 0;
  const onLeaveCount = statusCounts.on_leave || 0;
  const absentCount = statusCounts.absent || 0;
  const lateCount = statusCounts.late || 0;
  const activeNowCount = employees.filter(emp => (emp.status === 'present' || emp.status === 'late') && !emp.logout_time).length;
  const totalEmployees = stats?.total_employees || employees.length;
  const attendanceRate = totalEmployees ? Math.round((presentCount / totalEmployees) * 100) : 0;
  const totalWorkingMinutes = employees.reduce((sum, emp) => sum + calculateWorkingTime(emp), 0);
  const totalBreakMinutes = employees.reduce((sum, emp) => sum + calculateBreakTime(emp), 0);
  const averageWorkingMinutes = presentCount ? Math.floor(totalWorkingMinutes / presentCount) : 0;

  const formatMinutesCompact = (minutes) => {
    const hours = Math.floor((minutes || 0) / 60);
    const mins = (minutes || 0) % 60;
    return `${hours}h ${mins}m`;
  };

  const statCards = [
    {
      key: 'all',
      label: 'Total Employees',
      value: totalEmployees,
      sub: 'Active workforce',
      color: '#D4AF37',
      accent: 'rgba(212,175,55,0.34)',
      icon: <People />,
      progress: 100,
    },
    {
      key: 'present',
      label: 'Present Today',
      value: presentCount,
      sub: `${lateCount} late, ${activeNowCount} active now`,
      color: '#66BB6A',
      accent: 'rgba(102,187,106,0.32)',
      icon: <TrendingUp />,
      progress: attendanceRate,
    },
    {
      key: 'on_break',
      label: 'On Break',
      value: onBreakCount,
      sub: `${formatMinutesCompact(totalBreakMinutes)} total break`,
      color: '#FFB74D',
      accent: 'rgba(255,183,77,0.32)',
      icon: <Coffee />,
      progress: totalEmployees ? Math.round((onBreakCount / totalEmployees) * 100) : 0,
    },
    {
      key: 'away',
      label: 'On Leave / Absent',
      value: onLeaveCount + absentCount,
      sub: `${onLeaveCount} leave, ${absentCount} absent`,
      color: '#EF5350',
      accent: 'rgba(239,83,80,0.32)',
      icon: <EventBusy />,
      progress: totalEmployees ? Math.round(((onLeaveCount + absentCount) / totalEmployees) * 100) : 0,
    },
  ];

  const insightCards = [
    { label: 'Attendance Rate', value: `${attendanceRate}%`, tone: '#D4AF37', progress: attendanceRate },
    { label: 'Avg Work Time', value: formatMinutesCompact(averageWorkingMinutes), tone: '#66BB6A', progress: Math.min(100, Math.round((averageWorkingMinutes / 540) * 100)) },
    { label: 'Late Arrivals', value: lateCount, tone: '#FFB74D', progress: totalEmployees ? Math.round((lateCount / totalEmployees) * 100) : 0 },
  ];

  if (loading) {
    return (
      <Box sx={{ 
        p: 3, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A1929 0%, #0F2744 100%)',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          >
            <Schedule sx={{ fontSize: 60, color: '#D4AF37', mb: 2 }} />
          </motion.div>
          <Typography sx={{ color: '#D4AF37', fontWeight: 600, letterSpacing: 2 }}>
            LOADING ADMIN DASHBOARD...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{ 
        background: 'linear-gradient(135deg, #0A1929 0%, #0F2744 100%)', 
        minHeight: '100%',
      }}
      inert={statusDialogOpen || viewDialogOpen}
    >
      {/* Floating Refresh Button */}
      {!statusDialogOpen && !viewDialogOpen && (
        <Tooltip title="Refresh Data" placement="left">
          <Fab
            color="primary"
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
            }}
          >
            <Refresh />
          </Fab>
        </Tooltip>
      )}

      <Box sx={{ maxWidth: 1440, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#D4AF37', letterSpacing: 0.5 }}>
              Admin Dashboard
            </Typography>
            <Typography variant="body2" sx={{ color: '#8AA87D', mt: 0.5 }}>
              Live attendance overview & employee management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
            <Chip
              icon={<AccessTime sx={{ fontSize: 16 }} />}
              label={currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              sx={{
                height: 36,
                px: 0.5,
                bgcolor: 'rgba(212,175,55,0.09)',
                color: '#D4AF37',
                border: '1px solid rgba(212,175,55,0.22)',
                fontWeight: 700,
              }}
            />
            <Button
              startIcon={refreshing ? <CircularProgress size={18} sx={{ color: '#D4AF37' }} /> : <Refresh />}
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outlined"
              color="primary"
              sx={{ fontWeight: 700, minHeight: 36 }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          {statCards.map((stat, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -4 }}
              >
                <Card
                  onClick={() => setStatusFilter(stat.key)}
                  sx={{
                    minHeight: 162,
                    height: '100%',
                    cursor: 'pointer',
                    background: 'linear-gradient(145deg, rgba(16,43,73,0.96) 0%, rgba(9,23,38,0.98) 100%)',
                    border: `1px solid ${statusFilter === stat.key ? stat.color : stat.accent}`,
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: statusFilter === stat.key ? `0 18px 42px ${stat.color}22` : '0 12px 34px rgba(0,0,0,0.28)',
                    transition: 'border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '42%',
                      height: '100%',
                      background: `linear-gradient(135deg, transparent, ${stat.color}16)`,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: 2,
                      background: `linear-gradient(90deg, transparent, ${stat.color}, transparent)`,
                    },
                  }}
                >
                  <CardContent sx={{ position: 'relative', zIndex: 1, height: '100%', p: 2.25, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: '#8AA87D', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.1, display: 'block' }}>
                          {stat.label}
                        </Typography>
                        <motion.div
                          initial={{ scale: 0.5 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 200, delay: idx * 0.1 + 0.2 }}
                        >
                          <Typography sx={{ fontSize: { xs: 38, sm: 42 }, fontWeight: 900, color: stat.color, mt: 0.5, lineHeight: 1 }}>
                            {stat.value}
                          </Typography>
                        </motion.div>
                        <Typography variant="caption" sx={{ color: '#B0BEC5', opacity: 0.86, display: 'block', mt: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {stat.sub}
                        </Typography>
                      </Box>
                      <Avatar
                        sx={{
                          bgcolor: `${stat.color}20`,
                          width: 52,
                          height: 52,
                          color: stat.color,
                          border: `1px solid ${stat.color}50`,
                          backdropFilter: 'blur(10px)',
                          flexShrink: 0,
                          '& .MuiSvgIcon-root': { fontSize: 25 },
                        }}
                      >
                        {stat.icon}
                      </Avatar>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="caption" sx={{ color: '#8AA87D', fontWeight: 700 }}>Share</Typography>
                        <Typography variant="caption" sx={{ color: stat.color, fontWeight: 800 }}>{Math.min(100, stat.progress)}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, stat.progress)}
                        sx={{
                          height: 6,
                          borderRadius: 999,
                          bgcolor: 'rgba(255,255,255,0.08)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 999,
                            bgcolor: stat.color,
                          },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {insightCards.map((item, idx) => (
            <Grid item xs={12} md={4} key={item.label}>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 + idx * 0.06 }}>
                <Card
                  sx={{
                    borderRadius: 2,
                    minHeight: 96,
                    background: 'rgba(15,39,68,0.72)',
                    border: '1px solid rgba(212,175,55,0.14)',
                    boxShadow: '0 10px 28px rgba(0,0,0,0.2)',
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="body2" sx={{ color: '#8AA87D', fontWeight: 800 }}>{item.label}</Typography>
                      <Typography sx={{ color: item.tone, fontWeight: 900, fontSize: 22, lineHeight: 1 }}>{item.value}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, item.progress)}
                      sx={{
                        height: 7,
                        borderRadius: 999,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        '& .MuiLinearProgress-bar': { bgcolor: item.tone, borderRadius: 999 },
                      }}
                    />
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Real-time Employee Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card sx={{
            borderRadius: 2,
            overflow: 'hidden',
            background: 'rgba(15,39,68,0.78)',
            border: '1px solid rgba(212,175,55,0.16)',
            boxShadow: '0 18px 48px rgba(0,0,0,0.28)',
          }}>
            <CardContent sx={{ p: 0 }}>
              {/* Header Section */}
              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderBottom: '1px solid rgba(212,175,55,0.1)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      background: 'rgba(212,175,55,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(212,175,55,0.2)',
                    }}
                  >
                    <People sx={{ color: '#D4AF37', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#D4AF37', lineHeight: 1.2 }}>
                      Real-time Employee Status
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#8AA87D', opacity: 0.8 }}>
                      Live attendance updates • {filteredEmployees.length} of {employees.length} employees
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {statusFilter !== 'all' && (
                    <Button
                      size="small"
                      onClick={() => setStatusFilter('all')}
                      sx={{ color: '#B0BEC5', fontWeight: 700 }}
                    >
                      Clear Filter
                    </Button>
                  )}
                  {refreshing && <CircularProgress size={20} sx={{ color: '#D4AF37' }} />}
                </Box>
              </Box>

              {/* Search Bar */}
              <Box sx={{ p: { xs: 2, sm: 3 }, pt: 2, display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search by name, ID, department, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#D4AF37' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: '100%', sm: 420 },
                  }}
                />
                {[
                  { key: 'all', label: 'All' },
                  { key: 'present', label: 'Present' },
                  { key: 'on_break', label: 'Break' },
                  { key: 'late', label: 'Late' },
                  { key: 'away', label: 'Away' },
                ].map(item => (
                  <Chip
                    key={item.key}
                    label={item.label}
                    onClick={() => setStatusFilter(item.key)}
                    sx={{
                      height: 34,
                      px: 0.5,
                      fontWeight: 800,
                      color: statusFilter === item.key ? '#0A1929' : '#D4AF37',
                      bgcolor: statusFilter === item.key ? '#D4AF37' : 'rgba(212,175,55,0.08)',
                      border: '1px solid rgba(212,175,55,0.24)',
                      '&:hover': {
                        bgcolor: statusFilter === item.key ? '#F4D03F' : 'rgba(212,175,55,0.16)',
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Table */}
              <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: 600,
                    background: 'rgba(0,0,0,0.2)',
                    border: '1px solid rgba(212,175,55,0.1)',
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)',
                    '&::-webkit-scrollbar': {
                      width: 6,
                      height: 6,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(212,175,55,0.3)',
                      borderRadius: 3,
                    },
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 260 }}>Employee</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>Department</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>Work Type</TableCell>
                        <TableCell sx={{ minWidth: 130 }}>Status</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>Login Time</TableCell>
                        <TableCell sx={{ minWidth: 170 }}>Working Hours</TableCell>
                        <TableCell sx={{ minWidth: 170 }}>Break Time</TableCell>
                        <TableCell align="center" sx={{ minWidth: 86 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEmployees.length > 0 ? (
                        filteredEmployees.map((emp, idx) => {
                          const workingMinutes = calculateWorkingTime(emp);
                          const isActive = (emp.status === 'present' || emp.status === 'late') && !emp.logout_time && workingMinutes > 0;
                          const isOnBreak = emp.status === 'on_break';
                          const hasLogin = !!emp.login_time;
                          const statusStyle = getStatusColor(emp.status);

                          return (
                            <TableRow
                              component={motion.tr}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: Math.min(idx * 0.025, 0.25) }}
                              key={emp.id}
                              hover
                              sx={{
                                transition: 'background-color 0.2s ease',
                                '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' },
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar
                                    sx={{
                                      width: 44,
                                      height: 44,
                                      bgcolor: 'rgba(212,175,55,0.1)',
                                      color: '#D4AF37',
                                      fontWeight: 700,
                                      fontSize: '1rem',
                                      border: '1px solid rgba(212,175,55,0.24)',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {emp.first_name?.[0]}{emp.last_name?.[0]}
                                  </Avatar>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF', lineHeight: 1.3 }}>
                                      {emp.first_name} {emp.last_name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#8AA87D', opacity: 0.8 }}>
                                      {emp.employee_id}
                                    </Typography>
                                    {emp.email && (
                                      <Typography variant="caption" sx={{ color: '#8AA87D', opacity: 0.6, display: 'block', fontSize: '0.65rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {emp.email}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: '#B0BEC5', fontWeight: 500, whiteSpace: 'nowrap' }}>{emp.department || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  icon={emp.work_type === 'wfh' ? <Home sx={{ fontSize: 14 }} /> : <Business sx={{ fontSize: 14 }} />}
                                  label={emp.work_type?.toUpperCase() || 'WFO'}
                                  sx={{
                                    bgcolor: emp.work_type === 'wfh' ? 'rgba(156, 39, 176, 0.15)' : 'rgba(66, 165, 245, 0.15)',
                                    color: emp.work_type === 'wfh' ? '#CE93D8' : '#64B5F6',
                                    border: `1px solid ${emp.work_type === 'wfh' ? '#CE93D8' : '#64B5F6'}`,
                                    fontWeight: 600,
                                    height: 28,
                                    '& .MuiChip-icon': { mr: 0.5 },
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={getStatusLabel(emp.status)}
                                  sx={{
                                    ...statusStyle,
                                    fontWeight: 600,
                                    height: 28,
                                  }}
                                  icon={emp.status === 'on_break' ? <Coffee sx={{ fontSize: 14 }} /> : undefined}
                                />
                              </TableCell>
                              <TableCell sx={{ color: '#B0BEC5', fontWeight: 500, fontFamily: '"JetBrains Mono", monospace', whiteSpace: 'nowrap' }}>
                                {hasLogin ? formatTime(emp.login_time) : '-'}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ color: '#66BB6A', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                                    {formatWorkingTime(emp)}
                                  </Typography>
                                  {isActive && (
                                    <Chip
                                      size="small"
                                      label="ACTIVE"
                                      sx={{
                                        height: 22,
                                        fontSize: '0.55rem',
                                        fontWeight: 700,
                                        bgcolor: 'rgba(76, 175, 80, 0.15)',
                                        color: '#66BB6A',
                                        border: '1px solid #66BB6A',
                                        letterSpacing: '0.05em',
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ color: '#FFB74D', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                                    {formatBreakTime(emp)}
                                  </Typography>
                                  {emp.breaks && emp.breaks.length > 0 && (
                                    <Tooltip title={`${emp.breaks.length} break${emp.breaks.length > 1 ? 's' : ''} taken`}>
                                      <EventNote sx={{ fontSize: 16, color: '#FFB74D', opacity: 0.8 }} />
                                    </Tooltip>
                                  )}
                                  {isOnBreak && (
                                    <Chip
                                      size="small"
                                      label="BREAK"
                                      sx={{
                                        height: 22,
                                        fontSize: '0.55rem',
                                        fontWeight: 700,
                                        bgcolor: 'rgba(255, 152, 0, 0.15)',
                                        color: '#FFB74D',
                                        border: '1px solid #FFB74D',
                                        letterSpacing: '0.05em',
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleActionClick(e, emp)}
                                  sx={{
                                    color: '#D4AF37',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: 'rgba(212,175,55,0.15)',
                                      transform: 'scale(1.1)',
                                    },
                                  }}
                                >
                                  <MoreVert />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography variant="body1" sx={{ color: '#8AA87D' }}>
                              {searchTerm ? 'No employees match your search' : 'No employee data available'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor)}
        onClose={handleActionClose}
        elevation={4}
        sx={{
          '& .MuiPaper-root': {
            background: '#0F2744',
            border: '1px solid rgba(212,175,55,0.2)',
            borderRadius: 3,
            minWidth: 180,
          },
        }}
      >
        {['present', 'late', 'absent', 'on_leave'].map((status) => (
          <MenuItem
            key={status}
            onClick={() => handleStatusChange(status)}
            sx={{
              color: '#B0BEC5',
              transition: 'all 0.2s ease',
              '&:hover': { bgcolor: 'rgba(212,175,55,0.1)', color: '#D4AF37' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              {status === 'present' ? <CheckCircle sx={{ color: '#66BB6A' }} /> :
               status === 'late' ? <AccessTime sx={{ color: '#FFB74D' }} /> :
               status === 'absent' ? <Cancel sx={{ color: '#EF5350' }} /> :
               <EventNote sx={{ color: '#64B5F6' }} />}
            </ListItemIcon>
            <ListItemText 
              primary={`Mark as ${status.replace('_', ' ')}`}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
            />
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => handleViewDetails(selectedEmployee)}
          sx={{
            color: '#B0BEC5',
            transition: 'all 0.2s ease',
            '&:hover': { bgcolor: 'rgba(212,175,55,0.1)', color: '#D4AF37' },
          }}
        >
          <ListItemIcon sx={{ color: '#D4AF37', minWidth: 36 }}>
            <Visibility />
          </ListItemIcon>
          <ListItemText 
            primary="View Details"
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
          />
        </MenuItem>
      </Menu>

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: '#D4AF37', fontWeight: 700 }}>
          Confirm Status Change
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#B0BEC5' }}>
            Are you sure you want to mark <strong className="text-gold">{selectedEmployee?.first_name} {selectedEmployee?.last_name}</strong> as <strong className="text-gold">{selectedStatus?.replace('_', ' ')}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setStatusDialogOpen(false)}
            sx={{ color: '#B0BEC5', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleStatusSubmit}
            disabled={statusLoading}
            sx={{ fontWeight: 700 }}
          >
            {statusLoading ? <CircularProgress size={20} sx={{ color: '#000' }} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#D4AF37', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(212,175,55,0.15)',
              color: '#D4AF37',
              fontWeight: 700,
              border: '2px solid rgba(212,175,55,0.3)',
            }}
          >
            {viewEmployee?.first_name?.[0]}{viewEmployee?.last_name?.[0]}
          </Avatar>
          {viewEmployee?.first_name} {viewEmployee?.last_name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[
              { label: 'Employee ID', value: viewEmployee?.employee_id },
              { label: 'Email', value: viewEmployee?.email },
              { label: 'Department', value: viewEmployee?.department || '-' },
              { label: 'Work Type', value: viewEmployee?.work_type?.toUpperCase() || 'WFO' },
              { label: 'Status', value: getStatusLabel(viewEmployee?.status) },
              { label: 'Login Time', value: formatTime(viewEmployee?.login_time) },
              { label: 'Logout Time', value: formatTime(viewEmployee?.logout_time) },
              { label: 'Working Hours', value: formatWorkingTime(viewEmployee) },
              { label: 'Break Time', value: formatBreakTime(viewEmployee) },
              { label: 'Breaks Taken', value: viewEmployee?.breaks?.length || 0 },
            ].map((field, idx) => (
              <Grid item xs={6} key={idx}>
                <Box sx={{
                  p: 1.5,
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: 2,
                  border: '1px solid rgba(212,175,55,0.08)',
                }}>
                  <Typography variant="caption" sx={{ color: '#8AA87D', fontWeight: 500, display: 'block', mb: 0.3 }}>
                    {field.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                    {field.value || '-'}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setViewDialogOpen(false)}
            sx={{ fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
